import { type ReactNode, useState } from "react";
import { AuthProvider, hasAuthParams } from "react-oidc-context";

import { isSandboxPath, pathsSchema } from "shell/platform/config/paths";
import { useLoginFlows } from "shell/platform/contexts/LoginFlowContext";
import {
  type AuthProvider as AuthProviderValue,
  getUser,
  getUserManager,
  storeAuthProvider,
  storeLoginHint,
  storePmInfoFromUser,
} from "shell/platform/lib/oidcClient";

import { CheckUserSession } from "./CheckUserSession";

interface AuthGateProps {
  children: ReactNode;
  isEnabled?: boolean;
}

const hasActiveSession = () => {
  const user = getUser();
  return user !== null && !user.expired;
};

export const AuthGate = ({ children, isEnabled }: AuthGateProps) => {
  const { PasswordLogin, ProviderDiscovery } = useLoginFlows();
  const [isProviderSelected, setIsProviderSelected] = useState(false);
  const [passwordLoginEmail, setPasswordLoginEmail] = useState("");

  const handleProviderSelected = (
    selected: AuthProviderValue,
    email?: string,
  ) => {
    storeAuthProvider(selected);
    if (email) storeLoginHint(email);
    setIsProviderSelected(true);
  };

  if (globalThis.location.pathname.startsWith(pathsSchema.login)) {
    return children;
  }
  if (isSandboxPath(globalThis.location.pathname)) {
    return children;
  }
  if (!isEnabled) return children;

  if (passwordLoginEmail && !hasActiveSession()) {
    return (
      <PasswordLogin
        initialEmail={passwordLoginEmail}
        onAuthenticated={() => setIsProviderSelected(true)}
        onCancel={() => setPasswordLoginEmail("")}
      />
    );
  }

  if (!isProviderSelected && !hasActiveSession() && !hasAuthParams()) {
    return (
      <ProviderDiscovery
        onProviderSelected={handleProviderSelected}
        onPasswordLogin={setPasswordLoginEmail}
      />
    );
  }

  return (
    <AuthProvider
      userManager={getUserManager()}
      onSigninCallback={user => {
        globalThis.history.replaceState(
          {},
          document.title,
          globalThis.location.pathname,
        );
        if (user) storePmInfoFromUser(user);
      }}
    >
      <CheckUserSession>{children}</CheckUserSession>
    </AuthProvider>
  );
};
